import React, {Fragment, ReactElement, useRef, useState} from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {SidebarSectionHeader} from 'app/general/structures/DocSidebarPagePanels';
import {ReactRef} from 'app/general/types';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {SignoffDocumentStatsItem} from './SignoffDocumentStatsItem';
import {SignoffIndividualUsersPopover} from './SignoffIndividualUsersPopover';

import {
  BaseDocumentContentType,
  BasicUserSerializedType,
  SignoffDocumentSerializedType,
} from 'sweet-types';

import type {ExportableDocumentType} from 'app/signoffs/types';

interface SignoffDocumentStatsProps {
  document: ExportableDocumentType;
  signoffsList: Array<SignoffDocumentSerializedType>;
  loading: boolean;
}

export const SignoffDocumentStats = ({
  document,
  signoffsList,
  loading,
}: SignoffDocumentStatsProps): ReactElement => {
  const buttonRef: ReactRef<HTMLDivElement | null> = useRef(null);

  const [selectedUsers, setSelectedUsers] = useState<
    Array<BasicUserSerializedType>
  >([]);
  const [showSignoffPopover, setShowSignoffPopover] = useState<boolean>(false);

  return (
    <div>
      {showSignoffPopover === true && buttonRef.current != null ? (
        <SignoffIndividualUsersPopover
          overlayProps={{
            target: buttonRef.current,
            placement: 'bottom',
            isOpen: showSignoffPopover,
            toggle: () => setShowSignoffPopover(!showSignoffPopover),
          }}
          users={selectedUsers}
          objectId={document.id}
          contentType={document.content_type}
          onRequestSignoff={() => setSelectedUsers([])}
        />
      ) : null}
      <UserSignoffWrapperList
        allResults={signoffsList}
        loading={loading}
        contentType={document.content_type}
        buttonRef={buttonRef}
        selectedUsers={selectedUsers}
        setShowSignoffPopover={setShowSignoffPopover}
        setSelectedUsers={setSelectedUsers}
      />
    </div>
  );
};

function signoffStatsForCurrentVersion(
  results: Array<SignoffDocumentSerializedType>
): string {
  let signNumber = 0;

  results.map((signoff: SignoffDocumentSerializedType) => {
    if (
      signoff.signed_at != null &&
      signoff.current_created_at < signoff.signed_at
    ) {
      signNumber += 1;
    }
  });

  return signNumber + ' / ' + results.length;
}

function extractPossibleUserList(
  results: Array<SignoffDocumentSerializedType>
): Array<BasicUserSerializedType> {
  return results
    .filter(
      ({user, signed_at, current_signed_at}) =>
        user != null && (signed_at == null || current_signed_at == null)
    )
    .map(({user}) => user);
}

interface UserSignoffWrapperListProps {
  allResults: Array<SignoffDocumentSerializedType>;
  loading: boolean;
  contentType: BaseDocumentContentType;
  buttonRef: ReactRef<HTMLDivElement | null>;
  selectedUsers: Array<BasicUserSerializedType>;
  setShowSignoffPopover: (isOpen: boolean) => void;
  setSelectedUsers: (users: Array<BasicUserSerializedType>) => void;
}

const UserSignoffWrapperList = ({
  allResults,
  loading,
  contentType,
  buttonRef,
  selectedUsers,
  setShowSignoffPopover,
  setSelectedUsers,
}: UserSignoffWrapperListProps): ReactElement => {
  const ct = getContentType(contentType);

  const addSelectedUser = (userToAdd: BasicUserSerializedType) => {
    setSelectedUsers(selectedUsers.concat([userToAdd]));
  };

  const removeSelectedUser = (userToRemove: BasicUserSerializedType) => {
    setSelectedUsers(
      selectedUsers.filter((user) => user.id !== userToRemove.id)
    );
  };

  const possibleUserList = extractPossibleUserList(allResults);
  return (
    <ul className="list-unstyled">
      {!loading && allResults.length === 0 ? (
        <li>
          <NoResultsMessage
            onSidebar={true}
            icon="fa fa-signature"
            head={gt('No Sign Offs Requested')}
          >
            <p>
              {gt(
                "There haven't been any sign offs requested for this %(ct)s yet. When sign offs are requested, they'll appear here.",
                {ct: ct.name}
              )}
            </p>
          </NoResultsMessage>
        </li>
      ) : allResults.length !== 0 ? (
        <Fragment>
          <SidebarSectionHeader
            title={<div>{gt('overview')}</div>}
            help={
              <a href="/kb/8LBTequD/article/z8tofMoNq/not-all-team-members-have-been-asked-to-sign-off-a-document/">
                {gt('Missing sign offs?')}
              </a>
            }
            actionButton={
              <>
                <LazyTooltip
                  tooltip={gt(
                    'signed off latest request / total sign offs requested'
                  )}
                >
                  <span className="mr-2 badge badge-secondary">
                    {signoffStatsForCurrentVersion(allResults)}
                  </span>
                </LazyTooltip>

                {selectedUsers.length !== 0 ? (
                  <div style={{marginRight: -8}}>
                    <ActionsButton
                      ref={buttonRef}
                      actions={[
                        {
                          text: gt('Sign Off Reminder'),
                          icon: 'fa fa-signature',
                          key: 'request-signoffs-individual',
                          callback: () => setShowSignoffPopover(true),
                        },
                        {
                          text: gt('Deselect All'),
                          icon: 'far fa-square',
                          key: 'request-signoffs-deselect',
                          callback: () => setSelectedUsers([]),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <input
                    className="ml-2"
                    type="checkbox"
                    checked={selectedUsers.length === possibleUserList.length}
                    onChange={() => {
                      setSelectedUsers(
                        selectedUsers.length === possibleUserList.length
                          ? []
                          : possibleUserList
                      );
                    }}
                  />
                )}
              </>
            }
          />
          <ul className="list-unstyled">
            {allResults.map((item) => (
              <li key={'user-' + item.user.id}>
                <SignoffDocumentStatsItem
                  documentSignoff={item}
                  selected={
                    selectedUsers.filter((user) => user.id === item.user.id)
                      .length > 0
                  }
                  addUser={addSelectedUser}
                  removeUser={removeSelectedUser}
                />
              </li>
            ))}
          </ul>
        </Fragment>
      ) : null}
    </ul>
  );
};
